// Garderoba — what you wear, chosen at the szafa in the house. The farm's look (gameUi.js, owner
// 2026-10-03): a rounded cream panel under a wooden title, chunky tabs, rounded item cards that grow
// on hover. Items on the left (3 × 3 a page), your character on the right: the arrows turn them
// round, the walk box walks them on the spot. Bottom row: Zdejmij on the left, Zamknij on the right.
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { messageFor } from '../api/errors.js';
import { showToast } from './Toast.js';
import { NatalkaSprite } from './NatalkaSprite.js';
import { lockInput, unlockInput } from './inputLock.js';
import { iconText } from './iconText.js';
import { enlargeForTouch } from './touchEnlarge.js';
import { slotsFor, slotLabel } from './clothSlots.mjs';
import { FRONT, turn, walkVector, itemsPage } from './dressingPreview.mjs';
import { gamePanel, woodTitle, gameButton, gameCard, cardFeedback, pageArrows } from './gameUi.js';
import { FONT, PALETTE } from './gameUiStyle.mjs';
import { preloader } from '../scenes/PreloadScene.js';

const W = 780, H = 560;
const CARD_W = 116, CARD_H = 104, GAP = 12, COLS = 3;
const INK = '#4a2f14';

export function showDressingModal(scene) {
  const cx = GAME_WIDTH / 2, cy = GAME_HEIGHT / 2;
  const layer = scene.add.container(0, 0).setDepth(2000);
  lockInput(scene);

  const overlay = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
  const panelHit = scene.add.rectangle(cx, cy, W, H, 0xffffff, 0.001).setInteractive();   // a tap on the panel is not a tap on the overlay
  layer.add([overlay, panelHit, gamePanel(scene, cx, cy, W, H), woodTitle(scene, cx, cy - H / 2 + 4, '{dress} Garderoba', { fontSize: 24 })]);

  // The slots this character has clothes for, named for them (Ken: Koszulki / Kurtki / Spodnie …).
  const character = (typeof window !== 'undefined' ? window.__myCharacter : null) ?? 'natalka';
  const CATS = slotsFor(character, gameState.raw?.catalog?.clothes ?? {}).map((s) => ({ id: s.id, label: slotLabel(s.id, character) }));
  let activeCat = CATS[0]?.id ?? 'bluzka', page = 0;

  // Tabs: one row across the top, the open one in wood.
  const tabY = cy - H / 2 + 62;
  const TAB_STEP = Math.min(140, (W - 60) / Math.max(1, CATS.length));
  const tabs = CATS.map((c, i) => {
    const x = cx - (CATS.length - 1) * TAB_STEP / 2 + i * TAB_STEP;
    const b = gameButton(scene, x, tabY, TAB_STEP - 10, 38, c.label, () => { activeCat = c.id; page = 0; renderTabs(); renderItems(); }, { variant: 'secondary', fontSize: 15 });
    layer.add(b);
    return { id: c.id, b };
  });
  const renderTabs = () => { for (const t of tabs) t.b.setVariant(t.id === activeCat ? 'wood' : 'secondary'); };

  // Left: the items. Right: your character.
  const leftX = cx - W / 2 + 40 + (COLS * CARD_W + (COLS - 1) * GAP) / 2;   // centre of the item column
  const gridTop = cy - H / 2 + 104;
  const px = cx + W / 4 + 10, py = cy + 20;
  const bottomY = cy + H / 2 - 44;

  const itemsLayer = scene.add.container(0, 0);
  layer.add(itemsLayer);

  // A display figure, not your avatar (local: false): turning it here leaves your own facing alone.
  const preview = new NatalkaSprite(scene, px, py, { trackIdle: false, noTweakHud: true, local: false, look: window.__myLook ?? null });
  preview.setOutfit(gameState.raw.inventory.equipped);
  layer.add(preview);

  // Opens facing forward, standing (owner 2026-10-03).
  let facing = FRONT, walking = false;
  const pose = () => {
    preview.setFacing8(facing);
    if (walking) { const v = walkVector(facing); preview.setMotion(v.dx, v.dy); } else preview.setMotion(0, 0);
  };
  pose();
  for (const [way, dx] of [['left', -118], ['right', 118]]) {
    layer.add(gameButton(scene, px + dx, py, 52, 52, way === 'left' ? '{arrow_left}' : '{arrow_right}',
      () => { facing = turn(facing, way); pose(); }, { variant: 'wood', fontSize: 24, radius: 26 }));
  }
  // The walk box: a rounded tick box and the walking icon, no words (owner 2026-10-03).
  const wy = py + 152;
  const box = scene.add.graphics();
  const drawBox = () => {
    box.clear();
    box.fillStyle(PALETTE.panel.border, 1).fillRoundedRect(px - 34, wy - 15, 30, 30, 8);
    box.fillStyle(walking ? PALETTE.primary.face : 0xffffff, 1).fillRoundedRect(px - 31, wy - 12, 24, 24, 6);
  };
  drawBox();
  const tick = iconText(scene, px - 19, wy, '{check}', { fontFamily: FONT, fontSize: '16px' }).setOrigin(0.5).setVisible(false);
  const walkIcon = iconText(scene, px + 18, wy, '{walk}', { fontFamily: FONT, fontSize: '26px' }).setOrigin(0.5);
  const walkHit = scene.add.rectangle(px, wy, 100, 44, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  walkHit.on('pointerup', () => { walking = !walking; tick.setVisible(walking); drawBox(); pose(); });
  layer.add([box, tick, walkIcon, walkHit]);

  const unBtn = gameButton(scene, leftX, bottomY, 260, 46, '', async () => {
    try {
      await gameState.unequipClothing(activeCat);
      preview.setOutfit(gameState.raw.inventory.equipped);
      renderItems();
    } catch (e) { showToast(scene, messageFor(e)); }
  }, { variant: 'secondary', fontSize: 16 });
  const close = () => { unlockInput(scene); layer.destroy(); };
  const closeBtn = gameButton(scene, px, bottomY, 200, 46, 'Zamknij', close, { variant: 'wood', fontSize: 18 });
  layer.add([unBtn, closeBtn]);
  overlay.on('pointerup', close);

  function renderItems() {
    itemsLayer.removeAll(true);
    const owned = gameState.raw.inventory.clothes_owned[activeCat] ?? [];
    const equipped = gameState.raw.inventory.equipped[activeCat];
    const cat = gameState.raw.catalog.clothes ?? {};
    unBtn.setLabel(`Zdejmij: ${slotLabel(activeCat, character).toLowerCase()}`).setEnabled(!!equipped);

    if (owned.length === 0) {
      itemsLayer.add(iconText(scene, leftX, gridTop + CARD_H, 'Nic tu jeszcze nie masz.\nKup w sklepie ubrań.', {
        fontFamily: FONT, fontSize: '17px', color: INK, align: 'center',
      }).setOrigin(0.5));
      return;
    }
    const view = itemsPage(owned, page);
    page = view.page;
    const x0 = leftX - (COLS - 1) * (CARD_W + GAP) / 2;
    view.items.forEach((itemId, i) => {
      const x = x0 + (i % COLS) * (CARD_W + GAP), y = gridTop + CARD_H / 2 + Math.floor(i / COLS) * (CARD_H + GAP);
      const isEq = equipped === itemId;
      const card = gameCard(scene, x, y, CARD_W, CARD_H, { selected: isEq });
      const tex = `cloth_${itemId}`;
      const img = scene.add.image(x, y - 16, scene.textures.exists(tex) ? tex : 'plot_empty');
      const fit = () => { const src = img.texture.getSourceImage(); const k = Math.min(58 / src.width, 50 / src.height); img.setDisplaySize(src.width * k, src.height * k); };
      fit();
      // A phone does not prefetch the wardrobe's pictures: the card swaps its own in when it lands.
      if (!scene.textures.exists(tex)) preloader.whenReady(tex, () => { if (img.active) { img.setTexture(tex); fit(); } });
      // wrapped inside the card and bottom-anchored: a long name (Ken's white tee) grows upwards, at most two lines
      const name = iconText(scene, x, y + CARD_H / 2 - 8, cat[itemId]?.name_pl ?? itemId, {
        fontFamily: FONT, fontSize: '12px', color: INK, fontStyle: 'bold', wordWrap: { width: CARD_W - 14 }, align: 'center',
      }).setOrigin(0.5, 1);
      for (const fs of [12, 11, 10, 9]) { name.setFontSize(fs); if (name.height <= 2 * fs * 1.35 + 2) break; }
      // the card you wear is green and stays put; any other one puts that item on
      cardFeedback(scene, card, [img, name], isEq ? null : async () => {
        try {
          await gameState.equipClothing(itemId);
          preview.setOutfit(gameState.raw.inventory.equipped);
          renderItems();
        } catch (e) { showToast(scene, messageFor(e)); }
      });
      itemsLayer.add(card);
    });
    if (view.pages > 1) {
      const arrowsY = gridTop + 3 * (CARD_H + GAP) + 14;
      itemsLayer.add(pageArrows(scene, leftX, arrowsY, 70, page, view.pages, (p) => { page = p; renderItems(); }, { size: 40 }));
      itemsLayer.add(iconText(scene, leftX, arrowsY, `${page + 1} / ${view.pages}`, { fontFamily: FONT, fontSize: '15px', color: INK }).setOrigin(0.5));
    }
  }

  enlargeForTouch(scene, layer, { x: cx, y: cy, w: W, h: H }, { overlay });   // finger-sized on phones
  renderTabs();
  renderItems();
}
